import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import useLocalStorageSession from '@/hooks/useLocalStorageSession';
import { AccountContext } from '@/context/account.context';
import find from 'lodash/find';
import { getUrlEncodedPath } from '@/utils/utils';
import Auth from '@/utils/api/odas/auth';

const BASE_PATH = '/saml/sp/init/';
const SAML_PROVIDER_PREFIX = 'saml_';

/**
 * The start of the SSO flow: the API hands the member to their identity provider and brings them back
 * to `returnUrl` afterwards.
 */
export const getSAMLEntryUrl = (slug, returnUrl) => {
  return BASE_PATH + slug + (returnUrl ? '?r=' + returnUrl : '');
};

/**
 * Where to send a member whose session the app is ending on purpose. SAML members go straight back
 * through their identity provider, which is usually an invisible bounce, rather than to a login page
 * whose password field they cannot use; everyone else gets the login page.
 *
 * Read the provider *before* logging out — logging out clears it.
 */
export const getReturningSignInUrl = (provider, returnUrl) => {
  return provider?.startsWith(SAML_PROVIDER_PREFIX)
    ? getSAMLEntryUrl(provider.slice(SAML_PROVIDER_PREFIX.length), returnUrl)
    : `/login?r=${returnUrl}`;
};

/**
 * Send a member back through sign-in after the API has ended their session — the SSO flow for a SAML
 * member, the login page for anyone else.
 *
 * This deliberately does not go through useAccount's logout: that clears the account context, and pages
 * that redirect when they see a logged-out member (the SAML onboarding page among them) would race this
 * navigation and win. Dropping the session cookies is all that is needed, since signing back in replaces
 * them, and the member keeps work in progress that a real logout would discard.
 */
export const startReturningSignIn = (provider, returnUrl) => {
  const url = getReturningSignInUrl(provider, returnUrl);
  Auth.clearSessionCookies();
  window.location.assign(url);
};

/**
 * Provide logic to components that need to detect a login or signup attempt from an account
 * that should be redirected into the SAML SSO flow instead.
 */

const useSaml = () => {
  const { deleteSessionValue, sessionObject, setSessionValue } = useLocalStorageSession();
  const [lastSubmittedValue, setlastSubmittedValue] = useState('');
  const router = useRouter();
  const { loadSamlConfig, samlConfig, samlPartner, setSamlPartner, hasActiveSession, isLoggedIn } = useContext(AccountContext);

  useEffect(() => {
    // TODO useSaml probably should not load data if the person is not logged in
    // if (!hasActiveSession) {
    //   return;
    // }
    if (samlConfig === false) {
      loadSamlConfig();
      return;
    }
    let provider = false;
    if (router.query?.provider) {
      setSessionValue('provider', router.query?.provider);
      provider = router.query?.provider.replace('saml_', '');
    } else if (sessionObject.provider) {
      provider = sessionObject.provider.replace('saml_', '');
    }
    if (provider && samlConfig[provider]) {
      setSamlPartner(samlConfig[provider]);
    }
  }, [samlConfig, hasActiveSession]);

  const detectSamlPartner = (email) => {
    if (email && samlConfig) {
      let found = find(samlConfig, (partnerConfig) => {
        return partnerConfig.email_regex.test(email);
      });
      return found ? found : false;
    }
    return false;
  };

  const handleBlurOnEmailOrPhone = (event) => {
    let partner = detectSamlPartner(event.target.value);
    if (partner !== false) {
      setSamlPartner(partner);
      // was setting in local Storage but should not have persist
    } else {
      setSamlPartner(false);
      // probably do not need to unset from local storage if not setting, but should not hurt:
      deleteSessionValue('provider');
    }
  };

  const handleKeyPressOnEmailOrPhone = (event) => {
    // check if enter key and input has a new value
    if (event?.which == 13 && lastSubmittedValue !== event.target.value) {
      // if so, block submission and detectSamlPartner
      event.preventDefault();
      handleBlurOnEmailOrPhone(event);
      // also set lastSubmittedValue to current value
      setlastSubmittedValue(event.target.value);
    } else if (lastSubmittedValue !== event.target.value) {
      // clear samlPartner
      setSamlPartner(false);
      setSessionValue('provider', 'undefined');
    }
  };

  const handleSAMLRedirect = (event) => {
    if (samlPartner) {
      if (event?.preventDefault) {
        event.preventDefault();
      }
      // redirect to the beginning of the SSO flow
      // TODO: this is a XSS concern that should be tested with LADHS
      // We should probably confirm router.query.r is internal path before returning it
      const returnUrl = router.query?.r || getUrlEncodedPath();
      window.location.href = getSAMLEntryUrl(samlPartner.slug, returnUrl);
      return true;
    } else return false;
  };

  return {
    detectSamlPartner,
    handleBlurOnEmailOrPhone,
    handleKeyPressOnEmailOrPhone,
    handleSAMLRedirect,
    samlConfig,
    samlPartner
  };
};

export default useSaml;
